import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { toast } from 'react-toastify';
import cannedMessageService from './cannedMessageService';

const initialState = {
    cannedMessages: [],
    cannedMessage: null,
    isError: false,
    isSuccess: false,
    isLoading: false,
    message: "",
};

// Create canned message
export const createCannedMessage = createAsyncThunk(
    "cannedMessages/create",
    async (messageData, thunkAPI) => {
        try {
            return await cannedMessageService.createCannedMessage(messageData);
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get all canned messages
export const getCannedMessages = createAsyncThunk(
    "cannedMessages/getAll",
    async (params, thunkAPI) => {
        try {
            return await cannedMessageService.getCannedMessages(params);
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Get canned message by ID
export const getCannedMessageById = createAsyncThunk(
    "cannedMessages/getById",
    async (id, thunkAPI) => {
        try {
            return await cannedMessageService.getCannedMessageById(id);
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Update canned message
export const updateCannedMessage = createAsyncThunk(
    "cannedMessages/update",
    async ({ id, messageData }, thunkAPI) => {
        try {
            return await cannedMessageService.updateCannedMessage(id, messageData);
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

// Delete canned message
export const deleteCannedMessage = createAsyncThunk(
    "cannedMessages/delete",
    async (id, thunkAPI) => {
        try {
            return await cannedMessageService.deleteCannedMessage(id);
        } catch (error) {
            const message = (error.response && error.response.data && error.response.data.message) || error.message || error.toString();
            return thunkAPI.rejectWithValue(message);
        }
    }
);

export const cannedMessageSlice = createSlice({
    name: "cannedMessages",
    initialState,
    reducers: {
        RESET: (state) => {
            state.isError = false;
            state.isSuccess = false;
            state.isLoading = false;
            state.message = "";
        },
    },
    extraReducers: (builder) => {
        builder
            // Create
            .addCase(createCannedMessage.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(createCannedMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.cannedMessages.push(action.payload);
                toast.success("Canned message created successfully");
            })
            .addCase(createCannedMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
                toast.error(action.payload);
            })
            // Get all
            .addCase(getCannedMessages.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getCannedMessages.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.cannedMessages = action.payload;
            })
            .addCase(getCannedMessages.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
                toast.error(action.payload);
            })
            // Get by ID
            .addCase(getCannedMessageById.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(getCannedMessageById.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.cannedMessage = action.payload;
            })
            .addCase(getCannedMessageById.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
            })
            // Update
            .addCase(updateCannedMessage.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(updateCannedMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                const index = state.cannedMessages.findIndex(msg => msg._id === action.payload._id);
                if (index !== -1) {
                    state.cannedMessages[index] = action.payload;
                }
                toast.success("Canned message updated successfully");
            })
            .addCase(updateCannedMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
                toast.error(action.payload);
            })
            // Delete
            .addCase(deleteCannedMessage.pending, (state) => {
                state.isLoading = true;
            })
            .addCase(deleteCannedMessage.fulfilled, (state, action) => {
                state.isLoading = false;
                state.isSuccess = true;
                state.cannedMessages = state.cannedMessages.filter(msg => msg._id !== action.payload.id);
                toast.success("Canned message deleted successfully");
            })
            .addCase(deleteCannedMessage.rejected, (state, action) => {
                state.isLoading = false;
                state.isError = true;
                state.message = action.payload;
                toast.error(action.payload);
            });
    },
});

export const { RESET } = cannedMessageSlice.actions;
export default cannedMessageSlice.reducer;
